import { useEffect, useRef, useState, RefObject } from 'react';
import { CanvasInteropService } from '../services/canvasInteropService';
import { EngraveSettings, StrapDimensions, ValidationResult } from '../types';

/**
 * Canvas描画を管理するカスタムフック
 * @param canvasRef Canvas要素のRef
 * @param settings 彫刻設定
 * @param strap ストラップ寸法（ズームレベルに応じてスケーリング済み）
 * @param isFrontFace 前面かどうか（デフォルト: true）
 * @param onValidationChange バリデーション結果が変更されたときのコールバック（オプション）
 * @returns 読み込み状態とバリデーション結果
 */
export const useCanvasPreview = (
  canvasRef: RefObject<HTMLCanvasElement | null>,
  settings: EngraveSettings,
  strap: StrapDimensions,
  isFrontFace: boolean = true,
  onValidationChange?: (result: ValidationResult) => void
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult>({
    isValid: true,
    errors: [],
    warnings: []
  });
  const serviceRef = useRef<CanvasInteropService | null>(null);
  const renderIdRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // 前のタイマーをクリア
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 描画リクエストIDをインクリメント
    renderIdRef.current += 1;
    const currentRenderId = renderIdRef.current;

    const renderPreview = async () => {
      // 最新のリクエストかどうかを再確認
      if (currentRenderId !== renderIdRef.current) {
        return;
      }

      setIsLoading(true);
      try {
        if (!serviceRef.current) {
          serviceRef.current = new CanvasInteropService();
        }

        // 前面か後面かに応じてSVGパスを決定
        const svgPath = isFrontFace ? '/Butter_front_minify.svg' : '/Butter_back_minify.svg';

        const result = await serviceRef.current.renderPreviewWithLayout(
          canvas,
          settings,
          strap,
          svgPath
        );

        // 最新のリクエストの結果のみを反映
        if (currentRenderId === renderIdRef.current) {
          setValidationResult(result);
          onValidationChange?.(result);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('プレビュー描画エラー:', error);
        // 最新のリクエストの結果のみを反映
        if (currentRenderId === renderIdRef.current) {
          const errorResult = {
            isValid: false,
            errors: ['プレビューの生成に失敗しました'],
            warnings: []
          };
          setValidationResult(errorResult);
          onValidationChange?.(errorResult);
          setIsLoading(false);
        }
      }
    };

    // デバウンス処理: 100ms待ってから描画
    debounceTimerRef.current = setTimeout(renderPreview, 100);

    // クリーンアップ関数
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.text, settings.fontFamily, settings.fontSize, settings.alignment,
      settings.isBold, settings.isItalic, settings.isVertical,
      settings.horizontalAlignment, settings.verticalAlignment,
      settings.isImageMode, settings.imageData, isFrontFace, strap]);

  return { isLoading, validationResult };
};
